import { useEffect, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';

import type { CustomerSubscription } from '@models/customers';
import type {
  SubscriptionEvent,
  SubscriptionPeriod,
  SubscriptionPeriodPayment,
} from '@models/subscriptions';
import { SubscriptionPeriodStatuses } from '@models/subscriptions';
import { UserPermissions } from '@models/user';
import { formatShortDate, getSubscriptionLabel, humanizeLabel } from '@features/customers/helpers';
import {
  formatPaymentAmount,
  formatPaymentDate,
  getUiPaymentStatus,
} from '@features/payments/helpers';
import { useSubscriptionDetailsQuery } from '@features/subscriptions/queries';
import { useMeStore } from '@features/users/store';
import { userHasPermission } from '@utils/access';

import LoadingAnimation from '@components/LoadingAnimation';
import PaymentStatusBadge from '@components/Payments/PaymentStatusBadge';
import StatusChip, { type StatusChipTone } from '@components/ui/StatusChip';
import SubscriptionBadge from '../SubscriptionBadge';
import SubscriptionPeriodPaymentDrawer from './SubscriptionPeriodPaymentDrawer';
import s from './SubscriptionDetailDrawer.module.css';

const COPIED_RESET_MS = 1500;

const PERIOD_STATUS_TONES: Record<string, StatusChipTone> = {
  [SubscriptionPeriodStatuses.PAID]: 'success',
  [SubscriptionPeriodStatuses.PLANNED]: 'neutral',
  [SubscriptionPeriodStatuses.PAUSED]: 'warning',
};

type DrawerTab = 'periods' | 'events';

const TABS: { key: DrawerTab; label: string }[] = [
  { key: 'periods', label: 'Periods' },
  { key: 'events', label: 'Events' },
];

type SubscriptionDetailDrawerProps = {
  subscription: CustomerSubscription | null;
  onClose: () => void;
};

function formatDateTimeLabel(value: string | null): string {
  if (!value) return 'N/A';
  const fd = formatPaymentDate(new Date(value));
  return `${fd.date} · ${fd.time}`;
}

function PeriodPaymentCell({
  payment,
  onOpen,
}: {
  payment: SubscriptionPeriodPayment | null;
  /** Omitted for read-only users, who see the payment but cannot open its detail overlay. */
  onOpen?: (payment: SubscriptionPeriodPayment) => void;
}) {
  if (!payment) return <span className={s.muted}>—</span>;

  const content = (
    <>
      <span className={s.paymentAmount}>
        {formatPaymentAmount(payment.amount, payment.currency, payment.currency.decimalDigits)}
      </span>
      <PaymentStatusBadge status={getUiPaymentStatus(payment)} />
    </>
  );

  if (!onOpen) return <span className={s.paymentPlain}>{content}</span>;

  return (
    <button
      type="button"
      className={s.paymentLink}
      onClick={() => onOpen(payment)}
      title="Open payment details">
      {content}
    </button>
  );
}

function PeriodsTable({
  periods,
  onOpenPayment,
}: {
  periods: SubscriptionPeriod[];
  onOpenPayment?: (payment: SubscriptionPeriodPayment) => void;
}) {
  if (periods.length === 0) return <div className={s.tabEmpty}>No periods.</div>;

  return (
    <table className={s.miniTable}>
      <thead>
        <tr>
          <th>Start</th>
          <th>End</th>
          <th>Cycle</th>
          <th>Status</th>
          <th>Payment</th>
        </tr>
      </thead>
      <tbody>
        {periods.map((period) => (
          <tr key={period.id}>
            <td>{formatDateTimeLabel(period.startAt)}</td>
            <td>{formatDateTimeLabel(period.endAt)}</td>
            <td>{period.cycle ?? <span className={s.muted}>—</span>}</td>
            <td>
              {period.status ? (
                <StatusChip
                  label={humanizeLabel(period.status)}
                  tone={PERIOD_STATUS_TONES[period.status] ?? 'neutral'}
                />
              ) : (
                <span className={s.muted}>—</span>
              )}
            </td>
            <td>
              <PeriodPaymentCell payment={period.payment} onOpen={onOpenPayment} />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function EventsTable({ events }: { events: SubscriptionEvent[] }) {
  if (events.length === 0) return <div className={s.tabEmpty}>No events.</div>;

  return (
    <table className={s.miniTable}>
      <thead>
        <tr>
          <th>Created</th>
          <th>Action</th>
          <th>Reason</th>
        </tr>
      </thead>
      <tbody>
        {events.map((event) => (
          <tr key={event.id}>
            <td>{formatDateTimeLabel(event.createdAt)}</td>
            <td>
              <div className={s.cellStack}>
                <div>{humanizeLabel(event.action)}</div>
                {event.internalUserEmail && (
                  <div className={s.sub}>by {event.internalUserEmail}</div>
                )}
              </div>
            </td>
            <td className={s.reasonCell}>{event.reason || <span className={s.muted}>—</span>}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function SubscriptionDetailDrawer({ subscription, onClose }: SubscriptionDetailDrawerProps) {
  const [tab, setTab] = useState<DrawerTab>('periods');
  const [copied, setCopied] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState<SubscriptionPeriodPayment | null>(null);

  const { data, isLoading, isError } = useSubscriptionDetailsQuery(
    subscription
      ? { subscriptionId: subscription.id, subscriptionCreatedAt: subscription.createdAt }
      : undefined,
  );
  const me = useMeStore();

  // Same rule as the payments tab: read-only users can see payments but not open the payment overlay.
  const canViewPaymentDetails = !!me && userHasPermission(me, UserPermissions.EDIT_USERS);

  useEffect(() => {
    setTab('periods');
    setCopied(false);
    setSelectedPayment(null);
  }, [subscription?.id]);

  useEffect(() => {
    if (!subscription) return;
    const onKey = (e: KeyboardEvent) => {
      // While a payment is stacked on top, Escape belongs to that drawer.
      if (e.key === 'Escape' && !selectedPayment) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [subscription, selectedPayment, onClose]);

  if (!subscription) return null;

  const copyId = () => {
    navigator.clipboard
      .writeText(subscription.id)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), COPIED_RESET_MS);
      })
      .catch(() => {});
  };

  return (
    <>
      <div className={s.scrim} onClick={onClose} />
      <aside className={s.drawer} role="dialog" aria-label="Subscription details">
        <header className={s.headerBlock}>
          <div className={s.idRow}>
            <span className={s.subPill}>Subscription</span>
            <span className={s.idMono} title={subscription.id}>
              {subscription.id}
            </span>
            <button
              type="button"
              className={s.idCopy}
              onClick={copyId}
              aria-label="Copy subscription ID">
              {copied ? (
                <CheckIcon sx={{ fontSize: 14 }} />
              ) : (
                <ContentCopyIcon sx={{ fontSize: 14 }} />
              )}
            </button>
            <button type="button" className={s.close} onClick={onClose} aria-label="Close">
              <CloseIcon sx={{ fontSize: 18 }} />
            </button>
          </div>

          <div className={s.titleRow}>
            <h2 className={s.title}>
              {getSubscriptionLabel(subscription) ||
                subscription.paymentDescriptor ||
                'Subscription'}
            </h2>
            <span className={s.badgeWrap}>
              <SubscriptionBadge state={subscription.canceledAt ? 'canceled' : 'active'} />
            </span>
          </div>

          <div className={s.metaRow}>
            <span>
              <span className={s.metaLabel}>Paid till</span>
              {subscription.paidTill ? formatShortDate(subscription.paidTill) : 'N/A'}
            </span>
            {/* Migrated subscriptions carry their original start in startedAt; createdAt is the migration moment. */}
            <span>
              <span className={s.metaLabel}>Started</span>
              {formatShortDate(subscription.startedAt ?? subscription.createdAt)}
            </span>
            {subscription.canceledAt && (
              <span>
                <span className={s.metaLabel}>Canceled</span>
                {formatShortDate(subscription.canceledAt)}
                {subscription.canceledBy ? ` by ${subscription.canceledBy}` : ''}
              </span>
            )}
          </div>
        </header>

        <nav className={s.tabs} aria-label="Subscription sections">
          {TABS.map((t) => {
            const count = t.key === 'periods' ? data?.periods.length : data?.events.length;
            return (
              <button
                key={t.key}
                type="button"
                className={`${s.tab} ${tab === t.key ? s.tabActive : ''}`}
                onClick={() => setTab(t.key)}
                aria-pressed={tab === t.key}>
                {t.label}
                {count ? <span className={s.tabCount}>{count}</span> : null}
              </button>
            );
          })}
        </nav>

        {isLoading ? (
          <div className={s.loadingWrap}>
            <LoadingAnimation compact />
          </div>
        ) : isError ? (
          <p className={s.errorText}>Failed to load subscription periods and events.</p>
        ) : (
          <div className={s.tabPanel}>
            {tab === 'periods' ? (
              <PeriodsTable
                periods={data?.periods ?? []}
                onOpenPayment={canViewPaymentDetails ? setSelectedPayment : undefined}
              />
            ) : (
              <EventsTable events={data?.events ?? []} />
            )}
          </div>
        )}
      </aside>

      {selectedPayment && (
        <SubscriptionPeriodPaymentDrawer
          payment={selectedPayment}
          onClose={() => setSelectedPayment(null)}
        />
      )}
    </>
  );
}

export default SubscriptionDetailDrawer;
